<script setup lang="ts">
import isEmpty from "lodash/isEmpty";
import {
  AuthenticityMethod,
  FramingType,
  type ProductDetails,
  SalesMethod,
  ShippingType,
} from "mws-ts-v2-sdk";

import { emptyGuid } from "~/constants/misc";

type Props = {
  product: ProductDetails;
  productStarted?: boolean;
  hideShipping?: boolean;
  hideFraming?: boolean;
};

const { product, hideShipping, hideFraming } = defineProps<Props>();

const { sortedBids, obfuscatedUserIdForProduct } = useProductBids(
  () => product,
);

const nonEmptyProductDetails = computed(() => {
  if (!product.details) return null;

  return getObjectWithNonEmptyProperties(product.details);
});

const hasDetailsToShow = computed(() => {
  const hasDescriptionToShow = product.description;
  const hasProductDetailsToShow =
    nonEmptyProductDetails.value && !isEmpty(nonEmptyProductDetails.value);
  const hasBeneficiaryToShow = product.beneficiary?.name;

  const shouldShowFraming =
    product.framingType === FramingType.Frameable &&
    product.salesMethod !== SalesMethod.FixedPrice &&
    !hideFraming;

  const shouldShowShipping =
    (product.shippingType !== ShippingType.NoShipping ||
      !!product.shippingDescription) &&
    !hideShipping;

  const shouldShowAuthenticity =
    product.authenticityMethod === AuthenticityMethod.Fabricks;

  // TODO replace this with backend label.description when backend is localized
  const shouldShowLabelDescription = product.labels?.length;

  return {
    description: hasDescriptionToShow,
    details: hasProductDetailsToShow,
    beneficiary: hasBeneficiaryToShow,
    framing: shouldShowFraming,
    shipping: shouldShowShipping,
    authenticity: shouldShowAuthenticity,
    labelDescription: shouldShowLabelDescription,
  };
});
// TODO replace this with backend label.description when localized
// This should be sent to us by the backend, but they have not
// yet localized their label descriptions -_-
const hasMatchIssuedLabel = computed(() => {
  if (!product.labels?.length) return;

  return !!product.labels.find((label) => label.text === "Issued");
});
</script>

<template>
  <div class="flex flex-col bg-platform-bg-1 mt-2 md:mt-8">
    <!-- TODO replace this with backend label.description when localized -->
    <div
      v-if="hasDetailsToShow.labelDescription && hasMatchIssuedLabel"
      class="product-description__label-descriptions px-3 pt-4 md:pt-0 md:px-0"
    >
      <p class="product-description__label-descriptions__match-issued">
        {{ $t("product.labels.descriptions.matchIssued") }}
      </p>
    </div>

    <div
      v-if="hasDetailsToShow.description"
      class="text-platform-text-1 pt-4 md:pt-0 px-4 md:px-0"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div v-html="product.description" />
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <div class="px-4 md:px-0 pt-4 product-description-drawers">
      <ProductDescriptionDetailsDrawer
        v-if="hasDetailsToShow.details"
        :details="nonEmptyProductDetails"
        :product="product"
      />
      <ProductBidList
        v-if="productStarted && sortedBids.length > 0"
        :sorted-bids="sortedBids"
        :product="product"
        :obfuscated-user-id="obfuscatedUserIdForProduct"
      />

      <div class="md:hidden">
        <EngagementTrustPilotWidget
          variant="micro-star"
          class="scale-90"
        />
      </div>

      <ProductDescriptionShippingDrawer
        v-if="hasDetailsToShow.shipping"
        :product="product"
      />
      <ProductDescriptionEventDrawer
        v-if="product.eventId && product.eventId !== emptyGuid"
        :event-id="product.eventId"
      />
      <ProductDescriptionMoneyDrawer
        v-if="hasDetailsToShow.beneficiary && product.beneficiary"
        :beneficiary="product.beneficiary"
      />
      <ProductDescriptionFramingDrawer v-if="hasDetailsToShow.framing" />
      <ProductDescriptionAuthenticityDrawer
        v-if="hasDetailsToShow.authenticity"
      />
    </div>
  </div>
</template>

<style scoped lang="postcss">
.product-description-drawers {
  @apply flex flex-col gap-6;

  > div {
    @apply pb-6 border-b last:border-none;
  }
}
</style>
